/**
 * `<UploadToast />` — global floating panel that surfaces upload activity.
 *
 * Replaces the sidebar-mounted `<UploadIndicator />` surface (
 * second iteration). The store, selectors, side-table, timers and the
 * three upload wires (wizard / folder / library) are unchanged — only
 * the rendering surface moves from "small badge in sidebar footer" to
 * "self-anchored floating panel, bottom-right".
 *
 * Positioning: `fixed bottom-6 right-6 z-50`. The `z-50` token matches
 * the project convention for global floating UI established by
 * `features/analysis/ui/AnalysisNotifications` (the only other peer
 * with the same "global, store-driven, floating" shape).
 *
 * notistack coexistence: the project's `StyledSnackbarProvider` uses
 * notistack's default anchor (`{ vertical: 'bottom', horizontal: 'left' }`).
 * The toast lives on the opposite corner so there is no overlap and zero
 * coordination required between the two surfaces.
 *
 * Animations: monks-ui bakes `data-[state=open]:animate-in`-style
 * tailwind classes into its primitives, but `tailwindcss-animate` is NOT
 * installed in agent-studio, so those classes are no-ops. We use
 * `motion` (already a direct dependency) for the appear/expand
 * transitions to stay consistent with the rest of agent-studio.
 *
 * Header precedence mirrors the previous `<UploadStatusBadge />`:
 *   active > 0                → variant=active   (blue spinner + count)
 *   active === 0 & failed > 0 → variant=failed   (red destructive + count)
 *   active + failed === 0
 *     & recent success > 0    → variant=success  (green check, no count)
 *   else                      → renders nothing
 *
 * The toast itself does NOT schedule any timers. Auto-dismiss timing
 * (3s success, 1s cancelled, 10s failed) lives in the store; the toast
 * is a passive observer that disappears when `counts.total === 0`.
 */
import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { Spinner } from '@monksflow/monks-ui';
import { useRecentlyCompleted, useUploadCounts } from '../../model/selectors';
import { UploadTrayPanel } from '../UploadTrayPanel';
import { useTranslation } from 'react-i18next';

type Variant = 'active' | 'failed' | 'success';

function selectVariant(
  counts: ReturnType<typeof useUploadCounts>,
  recentSuccessCount: number,
): Variant | null {
  if (counts.active > 0) return 'active';
  if (counts.failed > 0) return 'failed';
  if (recentSuccessCount > 0) return 'success';
  return null;
}

const VARIANT_BG: Record<Variant, string> = {
  active: 'bg-card',
  failed: 'bg-card',
  success: 'bg-card',
};

const VARIANT_BADGE: Record<Variant, string> = {
  active: 'text-primary',
  failed: 'text-destructive',
  success: 'text-success',
};

export function UploadToast() {
  const { t } = useTranslation('projects');

  const counts = useUploadCounts();
  const recent = useRecentlyCompleted();
  const [expanded, setExpanded] = useState(false);

  const variant = selectVariant(counts, recent.length);

  // Empty queue → render nothing. AnimatePresence below handles the
  // exit transition for the LAST item leaving the queue.
  return (
    <AnimatePresence>
      {variant !== null && (
        <motion.div
          key="upload-toast"
          data-testid="upload-toast"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className={`fixed bottom-6 right-6 z-50 w-80 rounded-md border border-border-lighter shadow-lg ${VARIANT_BG[variant]}`}
        >
          <button
            type="button"
            data-testid="upload-toast-header"
            data-variant={variant}
            aria-expanded={expanded}
            aria-controls="upload-toast-body"
            onClick={() => setExpanded((prev) => !prev)}
            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
          >
            <span
              className={`flex items-center gap-2 text-sm font-medium ${VARIANT_BADGE[variant]}`}
            >
              {variant === 'active' && (
                <>
                  <span data-testid="upload-toast-spinner" className="inline-flex">
                    <Spinner />
                  </span>
                  <span>
                    {t('uploadQueue.uploadToast.uploading', { activeCount: counts.active })}
                  </span>
                </>
              )}
              {variant === 'failed' && (
                <span>{t('uploadQueue.uploadToast.uploadsFailed', { count: counts.failed })}</span>
              )}
              {variant === 'success' && (
                <>
                  <span
                    data-testid="upload-toast-check"
                    aria-label={t('uploadQueue.uploadToast.uploadsComplete')}
                  >
                    <CheckIcon className="size-4" />
                  </span>
                  <span>{t('uploadQueue.uploadToast.uploadsComplete')}</span>
                </>
              )}
            </span>
            <ChevronDownIcon
              className={`size-4 shrink-0 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </button>

          {expanded && (
            <div id="upload-toast-body" className="overflow-hidden border-t border-border-lighter">
              <UploadTrayPanel />
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
